import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from 'vue';
import store from '@/store';
import { usePermissionComposable } from '@/composables/usePermission';
import { Sds } from '@/interfaces/model/general/sds';

import {
  useSdsesExport,
  ExportSdsesTarget,
  ExportSdsesType,
  ExportSdsesCharacterCode,
  ExportOperations,
} from '@/composables/useSdsesExport';
import { commitAddNotification } from '@/store/main/mutations';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import { SdsesFilterParams } from '@/composables/useSdsFilter';
import { SdsFilters } from '@/interfaces/api/general/company';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { readUserLocale } from '@/store/main/getters';
import { EXPORT_FILE_FORMAT_ALL_CHAPTERS } from '@/lib/constant';
import { i18n as vI18n } from '@/plugins/vue-i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'ExportSdses',
  props: {
    operations: null,
    selectedSdses: null,
    keywordList: null,
    currentFilterParams: null,
    isSelectedWholeSdses: { type: Boolean },
    total: null
  },
  emits: ["successSubmitExportAction"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'successSubmitExportAction'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  operations: ExportOperations;
  selectedSdses: Sds[];
  keywordList: string[];
  currentFilterParams: SdsesFilterParams;
  isSelectedWholeSdses: boolean;
  total: number;
};

const { isFeatureActive, COMPANY_FEATURES } = useCompanyFeatureComposable();

const m = defineMessages({
  exportDialogTitle: '分析済みのデータをエクスポートしますか？',
  exportInstructions:
    'ご希望のエクスポート対象と種別を選択し「実行する」をクリックすると、ご自身のメールアドレスにファイルダウンロードURLが記載されたメールが送信されます。',
  exportTarget: 'エクスポート対象',
  allSds: '全てのSDS',
  selectedSds: '選択した{count}件のSDS',
  allChaptersCautionDetail:
    '2025年11月5日以前にアップロードされたSDSについてのデータ出力は「{0}」からお願いいたします。',
  exportSuccess:
    'エクスポートを受け付けました。お客様のメールアドレスにエクスポートしたファイルのURLが記載されたメールが届きますので、そちらのURLからファイルをダウンロードしてください。データ出力には最大で60分程度かかる可能性があります。',
  riskAssessmentExportSuccess:
    'リスク評価エクスポートを受け付けました。処理が完了次第、ご連絡いたします。',
  executeExport: 'エクスポートを実行',
  cautionCompletedOnly:
    'Excel, 標準CSV, 標準CREATE SIMPLE 製品DB CSVのファイルについては、ステータスが「データ化完了」のみデータがエクスポートされます。',
  cautionTime: 'データ出力には最大で60分程度かかる可能性があります。',
});





const isDialogOpen = ref<boolean>(false);
const isExporting = ref<boolean>(false);
const contactPageUrl = computed(() => {
  return vI18n.locale === 'ja-JP'
    ? process.env.VUE_APP_CONTACT_PAGE
    : process.env.VUE_APP_CONTACT_PAGE_EN;
});

const { hasPermission, PERMISSIONS: P } = usePermissionComposable();
const {
  exportSdsesTarget,
  exportSdsesType,
  exportSdsesFileFormat,
  exportSdsesExportFormat,
  selectableCharacterCode,
  exportSdsesCharacterCode,
  resetExportSdsesForm,
  validateExportSdses,
  convertFilterParamsToRequest,
  exportSdses,
  exportRiskAssessment,
} = useSdsesExport();
const { generateInactiveMessage } = useCompanyFeatureComposable();

const handleToggleDialog = () => {
  isDialogOpen.value = !isDialogOpen.value;
};

const handleCloseDialog = () => {
  isDialogOpen.value = false;
  resetExportSdsesForm();
};

const handleChangeExportTarget = (value: ExportSdsesTarget): void => {
  exportSdsesTarget.value = value;
};

const handleChangeExportType = (value: ExportSdsesType): void => {
  if (exportSdsesType.value !== value) {
    // エクスポート種別を変更した場合はファイル形式、エクスポート形式を選択し直しにする
    exportSdsesFileFormat.value = undefined;
    exportSdsesExportFormat.value = undefined;
  }
  exportSdsesType.value = value;
};

const handleChangeFileType = (value: string): void => {
  if (exportSdsesFileFormat.value !== value) {
    // ファイル形式を変更した場合は、エクスポート形式を選択し直しにする
    exportSdsesExportFormat.value = undefined;
  }
  exportSdsesFileFormat.value = value;
  selectableCharacterCode.value =
    exportSdsesType.value === 'risk_assessment'
      ? false
      : props.operations[exportSdsesType.value]?.find(
          (operation) => operation.value === exportSdsesFileFormat.value
        )?.selectable_character_code ?? false;
};

const handleChangeExportFormat = (value: string): void => {
  exportSdsesExportFormat.value = value;
};

const handleChangeExportCharacterCode = (
  value: ExportSdsesCharacterCode
): void => {
  exportSdsesCharacterCode.value = value;
};

const handleSubmitExport = async () => {
  if (exportSdsesType.value === 'risk_assessment') {
    isExporting.value = true;
    try {
      let params: SdsFilters = {
        ...convertFilterParamsToRequest(props.currentFilterParams),
      };
      params.keyword_list = props.keywordList;
      const response = await exportRiskAssessment(
        props.selectedSdses,
        params,
        props.isSelectedWholeSdses
      );
      if (response?.data) {
        commitAddNotification(store, {
          messageKey: m.riskAssessmentExportSuccess,
          color: 'success',
        });
        emits('successSubmitExportAction');
      }
    } catch (e) {
      console.error(e);
      return;
    } finally {
      isExporting.value = false;
    }
    handleCloseDialog();
    return;
  }

  const errorMessage = validateExportSdses();

  if (errorMessage) {
    commitAddNotification(store, {
      messageKey: errorMessage,
      color: 'error',
    });
    return;
  }

  isExporting.value = true;
  try {
    let params: SdsFilters = {
      ...convertFilterParamsToRequest(props.currentFilterParams),
    };
    params.keyword_list = props.keywordList;
    const response = await exportSdses(
      props.selectedSdses,
      params,
      props.isSelectedWholeSdses
    );

    if (response?.data) {
      commitAddNotification(store, {
        messageKey: m.exportSuccess,
        color: 'success',
      });

      emits('successSubmitExportAction');
    }
  } catch (e) {
    console.error(e);
    return;
  } finally {
    isExporting.value = false;
  }
  handleCloseDialog();
};

const countSelectedSds = computed((): number => {
  if (props.isSelectedWholeSdses) {
    return props.total;
  } else {
    return props.selectedSdses.length;
  }
});
const isRiskAssessmentEnabled = computed(() =>
  isFeatureActive(COMPANY_FEATURES.CHEMICAN_RISK_ASSESSMENT)
);

const isJapanese = computed(() => {
  const locale = readUserLocale(store)?.toLowerCase().trim();
  return (
    locale === 'ja' || locale.startsWith('ja-') || locale.startsWith('ja_')
  );
});

return { __sfc: true,isFeatureActive, COMPANY_FEATURES, m, props, emits, isDialogOpen, isExporting, contactPageUrl, hasPermission, P, exportSdsesTarget, exportSdsesType, exportSdsesFileFormat, exportSdsesExportFormat, selectableCharacterCode, exportSdsesCharacterCode, resetExportSdsesForm, validateExportSdses, convertFilterParamsToRequest, exportSdses, exportRiskAssessment, generateInactiveMessage, handleToggleDialog, handleCloseDialog, handleChangeExportTarget, handleChangeExportType, handleChangeFileType, handleChangeExportFormat, handleChangeExportCharacterCode, handleSubmitExport, countSelectedSds, isRiskAssessmentEnabled, isJapanese, sharedMessages, EXPORT_FILE_FORMAT_ALL_CHAPTERS }
}

})